import { createElement, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import ConfirmCancelHotkeyDialog from '../components/ConfirmCancelHotkeyDialog';

export type FormHotkeyId = 'F2' | 'Escape' | 'F4' | 'F5' | 'F6' | 'F7' | 'F8' | 'F9';

export interface FormHotkeyAction {
  handler: () => void;
  enabled?: boolean;
}

export type FormHotkeyMap = Partial<Record<FormHotkeyId, FormHotkeyAction>>;

export interface UseFormHotkeysResult {
  /** Mapa con ESC envuelto en confirmación; usarlo también en la barra de atajos. */
  hotkeys: FormHotkeyMap;
  /** Renderizar en el árbol de la pantalla para que aparezca el aviso. */
  confirmDialog: ReactNode;
}

/** Detecta diálogos MUI realmente visibles (no los keepMounted ocultos). */
function isMuiDialogOpen(): boolean {
  return Array.from(document.querySelectorAll('.MuiDialog-root')).some((node) => {
    if (!(node instanceof HTMLElement)) {
      return false;
    }
    if (node.getAttribute('aria-hidden') === 'true') {
      return false;
    }
    const style = window.getComputedStyle(node);
    if (style.display === 'none' || style.visibility === 'hidden') {
      return false;
    }
    return node.getClientRects().length > 0;
  });
}

function resolveHotkey(event: KeyboardEvent): FormHotkeyId | null {
  // Preferir event.code: más fiable con distintos layouts / teclas modificadoras
  const code = event.code;
  if (code === 'F2' || event.key === 'F2') return 'F2';
  if (code === 'F4' || event.key === 'F4') return 'F4';
  if (code === 'F5' || event.key === 'F5') return 'F5';
  if (code === 'F6' || event.key === 'F6') return 'F6';
  if (code === 'F7' || event.key === 'F7') return 'F7';
  if (code === 'F8' || event.key === 'F8') return 'F8';
  if (code === 'F9' || event.key === 'F9') return 'F9';
  if (event.key === 'Escape' || code === 'Escape') return 'Escape';
  return null;
}

interface UseFormHotkeysOptions {
  hotkeys: FormHotkeyMap;
  /** Si true, no se registran atajos (p. ej. modo solo lectura). */
  disabled?: boolean;
  /** Atajos que sí disparan aunque haya un Dialog abierto (p. ej. F4 para ciclar líneas). */
  allowWhenDialogOpen?: FormHotkeyId[];
  /** Si false, ESC cancela de inmediato sin alerta. Por defecto pide confirmación. */
  confirmEscape?: boolean;
}

function wrapEscapeWithConfirm(
  hotkeys: FormHotkeyMap,
  requestConfirm: (action: () => void) => void,
  confirmEscape: boolean,
): FormHotkeyMap {
  const escape = hotkeys.Escape;
  if (!confirmEscape || !escape) {
    return hotkeys;
  }
  return {
    ...hotkeys,
    Escape: {
      ...escape,
      handler: () => requestConfirm(escape.handler),
    },
  };
}

/**
 * Atajos de formulario estilo ERP (F2–F9, Escape).
 * Usa capture para ganar a comportamientos del navegador (p. ej. F7).
 * Lee handlers siempre desde ref para no quedar con closures viejos (p. ej. cobro agregado tras F2).
 * ESC abre una alerta; Enter confirma y ejecuta la cancelación.
 */
export function useFormHotkeys({
  hotkeys,
  disabled = false,
  allowWhenDialogOpen = [],
  confirmEscape = true,
}: UseFormHotkeysOptions): UseFormHotkeysResult {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const pendingEscapeRef = useRef<(() => void) | null>(null);
  const confirmOpenRef = useRef(false);
  const dismissConfirmRef = useRef<() => void>(() => undefined);

  const requestConfirm = useCallback((action: () => void) => {
    pendingEscapeRef.current = action;
    confirmOpenRef.current = true;
    setConfirmOpen(true);
  }, []);

  const dismissConfirm = useCallback(() => {
    pendingEscapeRef.current = null;
    confirmOpenRef.current = false;
    setConfirmOpen(false);
  }, []);
  dismissConfirmRef.current = dismissConfirm;

  const acceptConfirm = useCallback(() => {
    const action = pendingEscapeRef.current;
    pendingEscapeRef.current = null;
    confirmOpenRef.current = false;
    setConfirmOpen(false);
    action?.();
  }, []);

  const boundHotkeys = useMemo(
    () => wrapEscapeWithConfirm(hotkeys, requestConfirm, confirmEscape),
    [hotkeys, requestConfirm, confirmEscape],
  );

  const allowKey = allowWhenDialogOpen.join(',');
  const hotkeysRef = useRef(boundHotkeys);
  hotkeysRef.current = boundHotkeys;

  useEffect(() => {
    if (disabled) {
      return undefined;
    }

    const allowSet = new Set(allowKey ? (allowKey.split(',') as FormHotkeyId[]) : []);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (confirmOpenRef.current) {
        if (event.key === 'Escape' || event.code === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          dismissConfirmRef.current();
        }
        return;
      }

      const hotkeyId = resolveHotkey(event);
      if (!hotkeyId) {
        return;
      }

      const action = hotkeysRef.current[hotkeyId];
      if (!action || action.enabled === false) {
        return;
      }

      if (isMuiDialogOpen() && !allowSet.has(hotkeyId)) {
        return;
      }

      if (hotkeyId === 'Escape' && event.repeat) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      action.handler();
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [disabled, allowKey]);

  const confirmDialog = createElement(ConfirmCancelHotkeyDialog, {
    open: confirmOpen,
    onConfirm: acceptConfirm,
    onDismiss: dismissConfirm,
  });

  return { hotkeys: boundHotkeys, confirmDialog };
}
